import { AxiosError } from 'axios';
import React, { useImperativeHandle, useState } from 'react';
import { Alert, Badge, Form, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import { formatDate, formatNumberWithCommas } from 'utils/formats';
import { toastConfig } from 'utils/toastConfig';

export type UploadBankStatementFormHandle = {
  submitForm: () => Promise<'close' | 'results' | false>;
  reset: () => void;
};

export type UploadBankStatementRowResult = {
  checkNumber: string;
  statementAmount: number;
  bookDate?: string;
  supplierPaymentId?: number;
  paymentAmount?: number;
  status: string;
  message?: string;
};

export type UploadBankStatementResult = {
  totalCheckRows: number;
  confirmedCount: number;
  skippedCount: number;
  failedCount: number;
  rows: UploadBankStatementRowResult[];
};

type UploadBankStatementFormProps = {
  branchDomain: string;
  onUploaded?: () => void;
};

const statusBadge = (status: string) => {
  switch (status) {
    case 'Confirmed':
      return 'success';
    case 'AlreadyPaid':
      return 'info';
    case 'AmountMismatch':
    case 'NotFound':
    case 'Ambiguous':
    case 'InvalidStatus':
      return 'danger';
    default:
      return 'secondary';
  }
};

const UploadBankStatementForm = React.forwardRef<
  UploadBankStatementFormHandle,
  UploadBankStatementFormProps
>(({ branchDomain, onUploaded }, ref) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState<UploadBankStatementResult | null>(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    setIsUploading(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (selected && selected.length > 0) {
      setFile(selected[0]);
    }
  };

  const handleSubmit = async (): Promise<'close' | 'results' | false> => {
    if (result) {
      return 'close';
    }

    if (!file) {
      toast.error('Please select a bank statement file.', {
        ...toastConfig,
        autoClose: 3000
      });
      return false;
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('formFile', file);

      const { data } = await axiosInstance.post<UploadBankStatementResult>(
        `/api/v1/${branchDomain}/supplierPayments/uploadBankStatement`,
        formData
      );

      setResult(data);
      onUploaded?.();

      toast.success(
        `Processed ${data.totalCheckRows} check(s): ${data.confirmedCount} confirmed, ${data.skippedCount} skipped, ${data.failedCount} failed.`,
        { ...toastConfig, autoClose: 4000 }
      );

      return 'results';
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.errorMessage ||
            error.response?.data?.message ||
            'Failed to upload bank statement.',
          { ...toastConfig, autoClose: 3000 }
        );
      } else {
        toast.error('Failed to upload bank statement.', {
          ...toastConfig,
          autoClose: 3000
        });
      }
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    submitForm: handleSubmit,
    reset
  }));

  if (result) {
    const confirmedRows = result.rows.filter(row => row.status === 'Confirmed');

    return (
      <div>
        <Alert variant="light" className="border mb-3">
          <div className="d-flex flex-wrap gap-3">
            <span>
              <strong>{result.totalCheckRows}</strong> check rows
            </span>
            <span className="text-success">
              <strong>{result.confirmedCount}</strong> confirmed
            </span>
            <span className="text-info">
              <strong>{result.skippedCount}</strong> skipped
            </span>
            <span className="text-danger">
              <strong>{result.failedCount}</strong> failed
            </span>
          </div>
        </Alert>

        {confirmedRows.length > 0 ? (
          <div className="table-responsive" style={{ maxHeight: '360px' }}>
            <Table size="sm" hover className="fs-9 mb-0">
              <thead>
                <tr>
                  <th>Check #</th>
                  <th>Statement Amount</th>
                  <th>Payment Amount</th>
                  <th>Book Date</th>
                  <th>Status</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {confirmedRows.map((row, index) => (
                  <tr key={`${row.checkNumber}-${index}`}>
                    <td>{row.checkNumber}</td>
                    <td>{formatNumberWithCommas(row.statementAmount)}</td>
                    <td>
                      {row.paymentAmount != null
                        ? formatNumberWithCommas(row.paymentAmount)
                        : '—'}
                    </td>
                    <td>{row.bookDate ? formatDate(row.bookDate) : '—'}</td>
                    <td>
                      <Badge bg={statusBadge(row.status)}>{row.status}</Badge>
                    </td>
                    <td>{row.message || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <Alert variant="secondary" className="mb-0">
            No new payments were confirmed from this statement.
          </Alert>
        )}
      </div>
    );
  }

  return (
    <Form className="w-100">
      <Form.Group>
        <Form.Label>Bank Statement (CSV / Excel)</Form.Label>
        <Form.Control
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileChange}
          disabled={isUploading}
        />
        <Form.Text muted>
          Upload a bank statement with Check number, Amount, and Book date
          columns. Issued checks (Check Ready) and Check Released payments are
          marked as paid when the amount matches.
        </Form.Text>
      </Form.Group>
      {file && (
        <div className="mt-2 fs-9 text-body-secondary">Selected: {file.name}</div>
      )}
    </Form>
  );
});

UploadBankStatementForm.displayName = 'UploadBankStatementForm';

export default UploadBankStatementForm;
